import React, { useContext, useEffect, useState } from "react";
import {
  View,
  TouchableOpacity,
  FlatList,
  Platform} from "react-native";
import EmulateUserStyle from "./EmulateUserPageStyle";
import { ApiService } from "../../service/ApiService";
import Entypo from "@expo/vector-icons/Entypo";
import AsyncStorage from "@react-native-async-storage/async-storage";
import { UserContext } from "../../contexts/EmulateUserContext";
import SkeletonLoader from "../../components/SkeletonLoader";
import { IEmulateUser } from "../../types/IEmulateUserType";
import commonStyles from "../../styles/commonStyles";
import { getUserIdToUse } from "../../utils/getUserIdToUse";
import { useIsFocused } from "@react-navigation/native";
import { RefreshContext } from "../../contexts/refreshContext";
import { SafeAreaView } from "react-native-safe-area-context";
import AppText from "../../components/AppText";
import AppTextInput from "../../components/AppTextInput";

interface NavigationPageProps {
  navigation: any;
}

const EmulateUserPage: React.FC<NavigationPageProps> = ({ navigation }) => {
  const [partner, setPartner] = useState<IEmulateUser[]>([]);
  const [filteredPartners, setFilteredPartners] = useState<IEmulateUser[]>([]);
  const [emulateUserInfo, setEmulateUserInfo] = useState<IEmulateUser>();
  const { setEmulateUser } = useContext(UserContext);
  const [value, setValue] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isFocused, setIsFocused] = useState(false);
  const [selectedValue, setSelectedValue] = useState(false);
  const { refreshTrigger } = useContext(RefreshContext);
  const isPageFocused = useIsFocused();
  const [showNoResults, setShowNoResults] = useState(false); // New state for showing "no results" message

  useEffect(() => {
    const fetchPartnerData = async () => {
      setLoading(true);
      try {
        const userIdToUse = await getUserIdToUse();
        const response = await ApiService.get<IEmulateUser[]>(
          `portal/partner/emulationPartnerList/${userIdToUse}`
        );
        const data = response.data;
        setPartner(data);
        setLoading(false);
      } catch (err) {
        setError("Error fetching data");
        setLoading(false);
      }
    };
    if (isPageFocused) {
      fetchPartnerData();
    }
  }, [isPageFocused, refreshTrigger]);

  const handleSearch = (searchText: string) => {
    setValue(searchText);
    if (searchText.trim()) {
      const filteredData = partner.filter((item) =>
        item.fullName.toLowerCase().includes(searchText.toLowerCase())
      );
      setFilteredPartners(filteredData);
      setShowNoResults(filteredData.length === 0); // Show message if no results
    } else {
      setFilteredPartners([]);
      setShowNoResults(false); // Hide message when search is cleared
    }
  };

  const clearInput = () => {
    setValue("");
    setFilteredPartners([]);
    setSelectedValue(false);
    setShowNoResults(false); // Hide message when input is cleared
  };

  const emulateUser = async () => {
    try {
      if (emulateUserInfo) {
        const response = await ApiService.get<any>(
          `portal/partner/${emulateUserInfo.id}`
        );
        await AsyncStorage.setItem(
          "apiResponse",
          JSON.stringify(response.data)
        );
        await AsyncStorage.setItem(
          "emulatedUser",
          JSON.stringify(emulateUserInfo)
        );
        setEmulateUser(emulateUserInfo);
        navigation.reset({
          index: 0,
          routes: [{ name: "Investments" }]});
        setValue("");
        setFilteredPartners([]);
      }
    } catch (error) {
      console.error("Error emulating user:", error);
    }
  };

  const handleUserSelect = (item: any) => {
    setSelectedValue(true);
    setValue(`${item.fullName}\n${item.email}`);
    setEmulateUserInfo(item);
    setFilteredPartners([]);
    setShowNoResults(false); // Hide message when user is selected
  };

  return (
    <SafeAreaView edges={[ "bottom", "left", "right"]} style={[commonStyles.container, commonStyles.screenBottomGap]}>
      <View style={EmulateUserStyle.header}>
        <AppText style={EmulateUserStyle.headerText}>Emulate User</AppText>
      </View>
      <View style={EmulateUserStyle.autoCompleteContainer}>
        {loading ? (
          <View testID="skeleton-loader" style={{ height: 65 }}>
            <SkeletonLoader rows={1} rowHeight={85} />
          </View>
        ) : (
          <View style={EmulateUserStyle.inputContainer} testID="search-user">
            <AppTextInput
              style={[
                EmulateUserStyle.input,
                {
                  textAlignVertical: "top",
                  paddingLeft: 17,
                  paddingRight:17,
                  minHeight: 70},
                isFocused
                  ? EmulateUserStyle.focusedBorder
                  : EmulateUserStyle.defaultBorder,
                {
                  paddingTop: value.length > 0 ? 17 : 22, // Add padding when clear icon is shown
                },
              ]}
              value={value}
              onChangeText={handleSearch}
              autoComplete="off"
              onFocus={() => setIsFocused(true)}
              placeholder="Search User"
              placeholderTextColor="#CCC"
              multiline={true}
            />

            {value.length > 0 && (
              <TouchableOpacity
                onPress={clearInput}
                style={EmulateUserStyle.clearIcon}
                testID="clear-button"
              >
                <Entypo name="cross" size={24} color="#CCC" />
              </TouchableOpacity>
            )}
          </View>
        )}

        {showNoResults && !selectedValue && !loading && (
          <View style={EmulateUserStyle.noResultsContainer}>
            <AppText style={EmulateUserStyle.noResultsText}>User not found</AppText>
          </View>
        )}

        {filteredPartners.length > 0 && (
          <FlatList
            data={filteredPartners}
            renderItem={({ item }) => (
              <TouchableOpacity
                onPress={() => handleUserSelect(item)}
                style={EmulateUserStyle.option}
              >
                <AppText style={EmulateUserStyle.optionText}>
                  {item.fullName}
                  {"\n"}
                  <AppText style={EmulateUserStyle.emailText}>{item.email}</AppText>
                </AppText>
              </TouchableOpacity>
            )}
            keyExtractor={(item) => item.id.toString()}
            style={EmulateUserStyle.suggestionsList}
          />
        )}

        <TouchableOpacity
          style={[
            EmulateUserStyle.button,
            { backgroundColor: value ? "#3629B7" : "#A8A3D7" },
          ]}
          onPress={emulateUser}
          disabled={!value}
          accessibilityState={{ disabled: !value }}
          testID="emulate-button"
        >
          <AppText style={EmulateUserStyle.buttonText}>Emulate</AppText>
        </TouchableOpacity>
      </View>
      {error && (
        <View style={EmulateUserStyle.errorContainer}>
          <AppText style={EmulateUserStyle.errorText}>{error}</AppText>
        </View>
      )}
    </SafeAreaView>
  );
};

export default EmulateUserPage;
